/**
 * Sparkline: tiny inline time series.
 *
 * A hand-rolled SVG sparkline is ~30 lines and 0 dependencies, which
 * beats pulling in ECharts or similar for a strip next to each meter
 * row. Null values (empty buckets) are rendered as a faint dashed gap
 * so operators can tell missing data from genuinely flat data.
 */

import { useMemo } from "react";
import { useMantineTheme } from "@mantine/core";

import type { AggregateBucket } from "@/api/types";

export type SparklineProps = {
  buckets: AggregateBucket[];
  /** SVG width in px. */
  width?: number;
  /** SVG height in px. */
  height?: number;
  /** Stroke color; defaults to the theme primary shade. */
  color?: string;
  /** Optional ARIA label for screen readers. */
  label?: string;
};

export function Sparkline({
  buckets,
  width = 120,
  height = 32,
  color,
  label = "Recent values",
}: SparklineProps) {
  const theme = useMantineTheme();
  const stroke = color ?? theme.colors[theme.primaryColor]?.[6] ?? "#3b5bdb";

  const { path, dots } = useMemo(() => buildGeometry(buckets, width, height), [
    buckets,
    width,
    height,
  ]);

  if (buckets.length === 0) {
    return (
      <svg width={width} height={height} role="img" aria-label="No data">
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke={theme.colors.gray[4]}
          strokeDasharray="2 3"
          strokeWidth={1}
        />
      </svg>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label={label}
      style={{ overflow: "visible" }}
    >
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.5} />
      {dots.map((d, idx) => (
        <circle
          key={idx}
          cx={d.x}
          cy={d.y}
          r={d.empty ? 0 : 1.4}
          fill={d.empty ? "transparent" : stroke}
        />
      ))}
    </svg>
  );
}

type Point = { x: number; y: number; empty: boolean };

/**
 * Convert the aggregate grid into an SVG `d` attribute plus a list of
 * point markers. Empty buckets break the polyline into segments so
 * gaps read visually as absences instead of interpolated lines.
 */
function buildGeometry(
  buckets: AggregateBucket[],
  width: number,
  height: number,
): { path: string; dots: Point[] } {
  if (buckets.length === 0) return { path: "", dots: [] };

  const values = buckets.map((b) => b.avg);
  const populated = values.filter((v): v is number => v != null);
  if (populated.length === 0) return { path: "", dots: [] };

  const min = Math.min(...populated);
  const max = Math.max(...populated);
  const range = max - min || 1;
  // 10% vertical padding so dots never touch the frame.
  const pad = height * 0.1;
  const usable = height - pad * 2;

  const dx = buckets.length > 1 ? width / (buckets.length - 1) : 0;

  const dots: Point[] = buckets.map((b, i) => {
    const x = i * dx;
    if (b.avg == null) {
      return { x, y: height / 2, empty: true };
    }
    const y = pad + usable - ((b.avg - min) / range) * usable;
    return { x, y, empty: false };
  });

  // Build a path that restarts (`M`) after every empty bucket so gaps
  // show up as actual breaks rather than straight-line interpolation.
  let path = "";
  let penDown = false;
  for (const d of dots) {
    if (d.empty) {
      penDown = false;
      continue;
    }
    path += (penDown ? " L " : " M ") + `${d.x.toFixed(1)} ${d.y.toFixed(1)}`;
    penDown = true;
  }

  return { path: path.trim(), dots };
}
